import { IPlanification, IPlanificationProgressChart } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { ChartOptions } from 'chart.js';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { DefaultTheme } from 'styled-components';

interface Params {
  planification: IPlanification | undefined;
  planificationProgress: IPlanificationProgressChart | undefined;
  colors: DefaultTheme['colors'];
  t: TFunction<'StudentDashboardTranslations'>;
}

export const useChartOptions = ({
  planification,
  planificationProgress,
  colors,
  t
}: Params): ChartOptions<'line'> => {
  const chartOptions = useMemo<ChartOptions<'line'>>(() => {
    if (!planificationProgress) return {} as ChartOptions<'line'>;

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false
          },
          ticks: {
            callback: function (value) {
              if (!planificationProgress || !planification) return '';

              const totalLabels = planificationProgress.labels.length;
              const today = new Date().toISOString().split('T')[0];
              const todayIndex = planificationProgress.labels.findIndex(label => label === today);
              const middleIndex = Math.floor(totalLabels / 2);

              const tickValue = value as number;

              if (tickValue === 0) {
                return dateProvider.format(dateProvider.parse(planification.startDate), 'dd MMM');
              } else if (tickValue === (todayIndex !== -1 ? todayIndex : middleIndex)) {
                return t('TODAY');
              } else if (tickValue === totalLabels - 1) {
                return dateProvider.format(dateProvider.parse(planification.endDate), 'dd MMM');
              }

              return '';
            },
            color: colors.black6,
            font: {
              size: 11
            }
          },
          afterBuildTicks: function (scale) {
            if (!planificationProgress) return;

            const totalLabels = planificationProgress.labels.length;
            const today = new Date().toISOString().split('T')[0];
            const todayIndex = planificationProgress.labels.findIndex(label => label === today);
            const middleIndex = Math.floor(totalLabels / 2);

            // @ts-ignore – chart.js typing: ticks is any
            scale.ticks = [
              { value: 0 },
              { value: todayIndex !== -1 ? todayIndex : middleIndex },
              { value: totalLabels - 1 }
            ];
          }
        },
        y: {
          display: true,
          position: 'left',
          grid: {
            display: false
          },
          ticks: {
            callback: function (value) {
              if (!planificationProgress) return '';

              const maxValue = planificationProgress.totalItems;
              const halfValue = Math.round(maxValue / 2);
              if (value === 0) return '0%';
              if (value === halfValue) return '50%';
              if (value === maxValue) return '100%';

              return '';
            },
            color: colors.black6,
            font: {
              size: 11
            }
          },
          afterBuildTicks: function (scale) {
            if (!planificationProgress) return;

            const maxValue = planificationProgress.totalItems;
            const halfValue = Math.round(maxValue / 2);

            // @ts-ignore – chart.js typing: ticks is any
            scale.ticks = [
              { value: 0, label: '' },
              { value: halfValue, label: '' },
              { value: maxValue, label: '' }
            ];
          },
          min: 0,
          max: planificationProgress.totalItems
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: colors.white,
          titleColor: colors.black8,
          bodyColor: colors.black7,
          borderColor: colors.black2,
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: context => {
              const date = planificationProgress?.labels[context[0].dataIndex];
              if (date) {
                const dateObj = new Date(date);
                return dateObj.toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long'
                });
              }
              return '';
            },
            label: context => `${context.dataset.label}: ${context.parsed.y} items`
          }
        }
      },
      elements: {
        line: {
          borderCapStyle: 'round'
        }
      },
      layout: {
        padding: {
          right: 10,
          left: 10,
          bottom: 10
        }
      }
    } as ChartOptions<'line'>;
  }, [colors, planificationProgress, planification, t]);

  return chartOptions;
};
